// Forecast table

import React, { useMemo } from 'react';
import { ArrowDownTrayIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { ForecastResult } from '@/types/forecast';
import { formatPeriodForTable, getPeriodTypeName, getPeriodEndDate } from '@/utils/forecastUtils';


interface ForecastTableProps {
  results: ForecastResult[];
  isLoading: boolean;
  granularityMonths: number;
  includePlannedFailures?: boolean;
  className?: string;
}

export const ForecastTable: React.FC<ForecastTableProps> = ({
  results,
  isLoading: _isLoading,
  granularityMonths,
  includePlannedFailures = false,
  className = ''
}) => {

  // granularity
  const resultGranularity = results[0]?.granularityMonths || granularityMonths;

  // Get period type name
  const periodTypeName = useMemo(() => {
    return getPeriodTypeName(resultGranularity);
  }, [resultGranularity]);

  // Export to CSV
  const exportToCSV = () => {
    if (!results.length) return;

    const headers = [
      'Period',
      'Period Start',
      'Period End',
      'Predicted Shutdowns',
      ...(hasPlannedFailures ? ['Planned Maintenance'] : []),
      ...(hasPlannedFailures ? ['Total Shutdowns'] : []),
      'Lower CI',
      'Upper CI',
      'Cumulative Shutdowns',
      'Cumulative Lower CI',
      'Cumulative Upper CI'
    ];

    const csvRows = [headers.join(',')];

    // Get the start date from first result
    const firstResult = results[0];
    const startDate = firstResult?.periodStartDate ? new Date(firstResult.periodStartDate) : new Date();
    const forecastStartDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    results.forEach((result, index) => {
      const period = formatPeriodForTable(result, granularityMonths);
      const periodStartDate = new Date(result.periodStartDate).toLocaleDateString('en-US');

      // Calculate period end date
      const periodIndex = index * (result.granularityMonths || granularityMonths);
      const periodEnd = getPeriodEndDate(forecastStartDate, result.granularityMonths || granularityMonths, periodIndex);
      const periodEndDate = periodEnd.toLocaleDateString('en-US');

      const failures = result.expectedFailures.toFixed(2);
      const planned = result.plannedFailures?.toFixed(2) || '0';
      const total = (result.expectedFailures + (result.plannedFailures || 0)).toFixed(2);
      const lowerCI = result.lowerBound?.toFixed(2) || 'N/A';
      const upperCI = result.upperBound?.toFixed(2) || 'N/A';
      const cumulativeFailures = result.cumulativeFailures.toFixed(2);
      const cumulativeLowerCI = result.cumulativeLowerBound?.toFixed(2) || 'N/A';
      const cumulativeUpperCI = result.cumulativeUpperBound?.toFixed(2) || 'N/A';

      const row = [
        `"${period}"`, // Quote in case it contains commas
        periodStartDate,
        periodEndDate,
        failures,
        ...(hasPlannedFailures ? [planned] : []),
        ...(hasPlannedFailures ? [total] : []),
        lowerCI,
        upperCI,
        cumulativeFailures,
        cumulativeLowerCI,
        cumulativeUpperCI
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `forecast-results-${periodTypeName.toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // No data state
  if (!results.length) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Forecast Results</h3>
        <div className="text-center py-8">
          <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No forecast data</h4>
          <p className="text-gray-600">Calculate forecast to see detailed predictions</p>
        </div>
      </div>
    );
  }

  // Check if confidence intervals are available for periods and cumulative
  const hasPeriodCI = results.some(r =>
    r.lowerBound !== undefined && r.upperBound !== undefined
  );
  const hasCumulativeCI = results.some(r =>
    r.cumulativeLowerBound !== undefined && r.cumulativeUpperBound !== undefined
  );
  const hasPlannedFailures = includePlannedFailures && results.some(r =>
    r.plannedFailures !== undefined && r.plannedFailures > 0
  );

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Forecast Results
          </h3>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden">
        <div className="overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '180px' }}>
                  Period Range
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Predicted
                </th>
                {hasPlannedFailures && (
                  <>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Planned
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </>
                )}
                {hasPeriodCI && (
                  <>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lower CI
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Upper CI
                    </th>
                  </>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cumulative
                </th>
                {hasCumulativeCI && (
                  <>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cum. Lower CI
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cum. Upper CI
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {results.map((result, index) => (
                <tr
                  key={result.timePeriod}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatPeriodForTable(result, granularityMonths)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm text-gray-900">
                      {result.expectedFailures.toFixed(2)}
                    </div>
                  </td>
                  {hasPlannedFailures && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-amber-600">
                          {result.plannedFailures?.toFixed(2) || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900 font-medium">
                          {(result.expectedFailures + (result.plannedFailures || 0)).toFixed(2)}
                        </div>
                      </td>
                    </>
                  )}
                  {hasPeriodCI && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-600">
                          {result.lowerBound?.toFixed(2) || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-600">
                          {result.upperBound?.toFixed(2) || '—'}
                        </div>
                      </td>
                    </>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm text-gray-900 font-medium">
                      {result.cumulativeFailures.toFixed(2)}
                    </div>
                  </td>
                  {hasCumulativeCI && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-600">
                          {result.cumulativeLowerBound?.toFixed(2) || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-600">
                          {result.cumulativeUpperBound?.toFixed(2) || '—'}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer with summary */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>
            Showing {results.length} {periodTypeName.toLowerCase()}{results.length !== 1 ? 's' : ''}
            {(hasPeriodCI || hasCumulativeCI) && (
              <span>
                {' with '}
                {hasPeriodCI && 'period'}
                {hasPeriodCI && hasCumulativeCI && ' & '}
                {hasCumulativeCI && 'cumulative'}
                {' CIs'}
              </span>
            )}
          </span>
          <span>
            Total Expected: {results.reduce((sum, r) => sum + r.expectedFailures, 0).toFixed(1)} shutdowns
            {hasPlannedFailures && (
              <span className="ml-2">
                (+ {results.reduce((sum, r) => sum + (r.plannedFailures || 0), 0).toFixed(1)} planned)
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};